import FormInput from "components/FormInput";
import FormFileInput from "components/FormFileInput";
import FormSelect from "components/FormSelect";
import FormDate from "components/FormDate";
import FormTextareaOnDemand from "components/FormTextareaOnDemand";
import FormAutocomplete from "components/FormAutocomplete";
import CategoryAutocomplete from "screens/ItemCreateEdit/components/CategoryAutocomplete";

import itemInfoValidate from "screens/ItemCreateEdit/components/itemInfoValidate";

import { YEARS_LIST } from "lib/form/constants";
import { formatDateForDatePicker } from "lib/date/format";

const itemInfoFields = (statusList, selected, clientsList, onClientChange) => {
  const fields = [];
  
  const clientOptions = clientsList ? clientsList.filter(client => client.status === 'active') : [];
  const clientsWithLabel = clientOptions.map(client => ({
    ...client,
    displayLabel: `${client.code || 'N/A'} - ${client.name || 'No name'}`
  }));

  fields.push({
    name: "clientId",
    component: FormSelect,
    props: {
      externalLabel: true,
      label: "Client",
      placeholder: "Select a client",
      options: clientsWithLabel,
      valueAccessor: "_id",
      labelAccessor: "displayLabel",
      required: true,
      requiredMessage: "Please select a client",
      disabled: !!selected,
      initialValue: selected ? (selected.clientId ? (typeof selected.clientId === 'object' ? selected.clientId._id : selected.clientId) : "") : "",
      onChangeExternal: (value) => {
        if (onClientChange) onClientChange(value);
      },
      style: (() => {
        try {
          const q = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
          const id = q.get('clientId');
          if (!id) return {};
          const selectedClient = clientsWithLabel.find(c => (typeof c._id === 'string' ? c._id : c._id?.toString()) === (typeof id === 'string' ? id : id?.toString()));
          if (!selectedClient?.color || !/^#([0-9A-Fa-f]{6})$/.test(selectedClient.color)) return {};
          return { borderLeft: `6px solid ${selectedClient.color}` };
        } catch (e) {
          return {};
        }
      })(),
    },
  });
  
  if (selected) {
    fields.push({
      name: "SKU",
      component: FormInput,
      props: {
        externalLabel: true,
        label: "SKU",
        requiredMessage: "",
        disabled: true,
        readOnly: true,
        inputProps: {
          style: { padding: '5px' },
        },
      },
    });
  }
  
  const getDisabledProps = () => {
    if (selected) {
      return {};
    }
    return {
      dependsOnValue: "clientId",
      attributeToChange: "disabled",
      habilitateAttributeValues: ["", "-1"],
    };
  };

  const disabledProps = getDisabledProps();
  
  return [
    ...fields,
    {
      name: "category",
      component: CategoryAutocomplete,
      props: {
        externalLabel: true,
        label: "Category",
        requiredMessage: "A category must be selected",
        required: true,
        ...disabledProps,
      },
    },
    {
      name: "name",
      component: FormInput,
      props: {
        fullWidth: true,
        label: "Item name",
        placeholder: "Insert item name",
        required: true,
        externalLabel: true,
        ...disabledProps,
      },
    },
    {
      name: "model",
      component: FormInput,
      props: {
        fullWidth: true,
        label: "Model",
        placeholder: "Insert model name",
        required: true,
        externalLabel: true,
        ...disabledProps,
      },
    },
    {
      name: "manufacturer",
      component: FormInput,
      props: {
        fullWidth: true,
        label: "Manufacturer",
        placeholder: "Insert manufacturer name",
        required: true,
        externalLabel: true,
        ...disabledProps,
      },
    },
    {
      name: "yearManufactured",
      component: FormAutocomplete,
      props: {
        label: "Year manufactured",
        placeholder: "Select year",
        options: YEARS_LIST,
        optionLabelSelector: "label",
        optionValueSelector: "value",
        ...disabledProps,
      },
    },
    {
      name: "serialNumber",
      component: FormInput,
      props: {
        externalLabel: true,
        label: "Serial number",
        placeholder: "Insert item serial number",
        ...disabledProps,
      },
    },
    {
      name: "description",
      component: FormInput,
      props: {
        externalLabel: true,
        label: "Item description",
        placeholder: "Insert item description",
        multiline: true,
        rows: 5,
        ...disabledProps,
      },
    },
    {
      name: "pictures",
      component: FormFileInput,
      props: {
        label: "Item pictures",
        externalLabel: true,
        multiple: true,
        accept: "image/*",
        ...disabledProps,
      },
    },
    {
      name: "status",
      component: FormSelect,
      props: {
        label: "Item status",
        placeholder: "Select status",
        fullWidth: true,
        options: statusList,
        valueAccessor: "_id",
        labelAccessor: "name",
        initialValue: statusList.find((status) => status.name === "Draft")?._id,
        required: true,
        externalLabel: true,
        ...disabledProps,
      },
    },
    {
      name: "statusNoteField",
      component: FormTextareaOnDemand,
      props: {
        externalLabel: true,
        label: " ",
        placeholder: "Status note",
        initialValue: "",
        onClickButtonText: "Save note",
        notesField: "statusNote",
        ...disabledProps,
      },
    },
    {
      name: "source",
      component: FormInput,
      props: {
        externalLabel: true,
        label: "Source",
        placeholder: "Insert source",
        inputProps: {
          maxLength: 70,
        },
        ...disabledProps,
      },
    },
    {
      name: "dateOfAdquisition",
      component: FormDate,
      props: {
        externalLabel: true,
        label: "Acquisition date",
        placeholder: "Select date",
        inputProps: {
          max: formatDateForDatePicker(new Date()),
        },
        ...disabledProps,
      },
    },
  ];
};

const getInitialValues = (selected, clientsList) => {
  let initialValues;

  if (selected) {
    initialValues = {
      SKU: selected.number || "",
      name: selected.name,
      model: selected.model,
      manufacturer: selected.manufacturer,
      yearManufactured: selected.yearManufactured
        ? selected.yearManufactured.toString()
        : "",
      serialNumber: selected.serialNumber,
      description: selected.description,
      pictures: selected.pictures,
      status: selected.status?._id || "",
      statusNoteField: "",
      statusNote: selected.statusNote,
      source: selected.source,
      dateOfAdquisition: formatDateForDatePicker(selected.dateOfAdquisition),
      location: selected.location,
      category: selected.category?._id || "",
      clientId: selected.clientId ? (typeof selected.clientId === 'object' ? selected.clientId._id : selected.clientId) : "",
      imagesToDelete: [],
      selected: selected._id,
    };
  } else {
    initialValues = {
      clientId: "",
      name: "",
      model: "",
      manufacturer: "",
      yearManufactured: "",
      serialNumber: "",
      description: "",
      pictures: [],
      status: "",
      statusNote: [],
      statusNoteField: "",
      source: "",
      dateOfAdquisition: "",
      location: "no",
      category: "",
    };
  }

  return initialValues;
};

const itemInfoForm = (statusList, aaaa, selected, clientsList, onClientChange) => {
  return {
    fields: itemInfoFields(statusList, selected, clientsList, onClientChange),
    validate: (values) => itemInfoValidate(values, statusList, selected),
    displayLabel: "Item information",
    name: "itemInfo",
    initialValues: getInitialValues(selected, clientsList),
  };
};

export default itemInfoForm;
